// src/components/ReportProblem.js - Fixed version
import React, { useState, useRef, useEffect } from 'react';
import { AlertTriangle, Mail, User, Type, FileText, Lock, Send, Loader, RefreshCw } from 'lucide-react';
import { auth, db } from '../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import styles from './ReportProblem.module.css';

// Custom Captcha generator
const generateCaptchaData = () => {
  // Generate a random 6-character code
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let captchaText = '';
  for (let i = 0; i < 6; i++) {
    captchaText += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return { text: captchaText };
};

// Component to render the captcha image
const CaptchaImage = ({ captchaText }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !captchaText) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add noise (dots)
    for (let i = 0; i < 100; i++) {
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.arc(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        Math.random() * 2,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
    
    // Add lines
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `rgba(0, 0, 0, ${Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
    
    // Draw the text
    ctx.font = 'bold 24px Arial';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Add each character with slight rotation for better security
    for (let i = 0; i < captchaText.length; i++) {
      const x = (canvas.width / (captchaText.length + 1)) * (i + 1);
      const y = canvas.height / 2 + Math.random() * 6 - 3;
      
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((Math.random() * 30 - 15) * Math.PI / 180);
      ctx.fillText(captchaText.charAt(i), 0, 0);
      ctx.restore();
    }
    
  }, [captchaText]);
  
  return (
    <canvas 
      ref={canvasRef} 
      width={200} 
      height={60} 
      className={styles.captchaCanvas}
    />
  );
};

const ReportProblem = ({ userData }) => {
  const [formData, setFormData] = useState({
    username: userData?.username || '',
    subject: '',
    message: '',
    password: '',
    captcha: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [captcha, setCaptcha] = useState(generateCaptchaData());

  // Check if user is authenticated with Google
  useEffect(() => {
    const checkAuthProvider = () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const googleProvider = user.providerData.some(
            provider => provider.providerId === 'google.com'
          );
          setIsGoogleUser(googleProvider);
        }
      } catch (error) {
        console.error("Error checking auth provider:", error);
        // Default to captcha verification if we can't determine
        setIsGoogleUser(true);
      }
    };
    
    checkAuthProvider();
  }, []);

  const refreshCaptcha = () => {
    setCaptcha(generateCaptchaData());
    setFormData(prev => ({
      ...prev,
      captcha: ''
    }));
    // Clear captcha error if it exists
    if (errors.captcha) {
      setErrors(prev => ({
        ...prev,
        captcha: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    // Validate password for non-Google users
    if (!isGoogleUser && !formData.password) {
      newErrors.password = 'Password is required to verify your identity';
    }
    
    // Validate captcha for Google users
    if (isGoogleUser && !formData.captcha) {
      newErrors.captcha = 'Please enter the captcha text';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Verify user identity
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('User not authenticated properly.');
      }
      
      // Handle different verification methods based on auth provider
      if (isGoogleUser) {
        // For Google users, verify captcha
        if (formData.captcha.toLowerCase() !== captcha.text.toLowerCase()) {
          setErrors(prev => ({
            ...prev,
            captcha: 'Incorrect captcha. Please try again.'
          }));
          setLoading(false);
          refreshCaptcha();
          return;
        }
      } else {
        // For email/password users, verify password
        try {
          const credential = EmailAuthProvider.credential(user.email, formData.password);
          await reauthenticateWithCredential(user, credential);
        } catch (authError) {
          console.error("Auth error:", authError);
          
          if (authError.code === 'auth/wrong-password') {
            setErrors(prev => ({
              ...prev,
              password: 'Incorrect password. Please try again.'
            }));
          } else {
            setSubmitError('Authentication failed: ' + (authError.message || 'Please check your password'));
          }
          setLoading(false);
          return;
        }
      }
      
      // Store the report in Firestore
      try {
        await addDoc(collection(db, 'problemReports'), {
          userId: user.uid,
          username: formData.username,
          subject: formData.subject,
          message: formData.message,
          email: user.email,
          timestamp: serverTimestamp(),
          status: 'new'
        });
        
        // Log for debug purposes - remove in production
        console.log('Problem report submitted successfully');
        
        // Reset the form and show success message
        setFormData(prev => ({
          ...prev,
          subject: '',
          message: '',
          password: '',
          captcha: ''
        }));
        
        setSubmitted(true);
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        throw new Error("Failed to save your report. Please try again.");
      }
      
    } catch (error) {
      console.error('Error submitting problem report:', error);
      setSubmitError(error.message || 'An error occurred while submitting your report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmitError('');
    setErrors({});
    refreshCaptcha();
  };

  return (
    <div className={styles.reportProblemContainer}>
      <div className={styles.sectionContent}>
        <h1>Report a Problem</h1>
        
        <div className={styles.infoBox}>
          <AlertTriangle size={24} className={styles.infoIcon} />
          <div>
            <p>
              If you've encountered an issue with Linkist, please fill out this form. 
              Our team will investigate and respond as soon as possible.
            </p>
            <p>
              For urgent matters, you can also email us directly at{' '}
              <a href="mailto:problems@linkist.se" className={styles.emailLink}>
                problems@linkist.se
              </a>
            </p>
          </div>
        </div>
        
        {submitted ? (
          <div className={styles.successContainer}>
            <div className={styles.successMessage}>
              <Mail size={40} className={styles.successIcon} />
              <h2>Report Submitted!</h2>
              <p>
                Thank you for your report. Our team has been notified and will
                look into this issue as soon as possible.
              </p>
              <p>
                You may receive a response at your registered email address.
              </p>
              <button 
                className={styles.submitButton}
                onClick={resetForm}
              >
                Submit Another Report
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.reportForm}>
            <div className={styles.formGroup}>
              <label htmlFor="username" className={styles.formLabel}>
                <User size={18} />
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Your username"
                className={`${styles.formInput} ${errors.username ? styles.inputError : ''}`}
                disabled
              />
              {errors.username && <div className={styles.errorText}>{errors.username}</div>}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="subject" className={styles.formLabel}>
                <Type size={18} />
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Brief description of the issue"
                className={`${styles.formInput} ${errors.subject ? styles.inputError : ''}`}
              />
              {errors.subject && <div className={styles.errorText}>{errors.subject}</div>}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.formLabel}>
                <FileText size={18} />
                Describe the Problem
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please provide as much detail as possible about what happened..."
                className={`${styles.formTextarea} ${errors.message ? styles.inputError : ''}`}
                rows={5}
              />
              {errors.message && <div className={styles.errorText}>{errors.message}</div>}
            </div>
            
            {/* Conditional rendering based on auth provider */}
            {isGoogleUser ? (
              <div className={styles.formGroup}>
                <label htmlFor="captcha" className={styles.formLabel}>
                  <Lock size={18} />
                  Security Verification
                </label>
                <div className={styles.captchaContainer}>
                  <div className={styles.captchaBox}>
                    <CaptchaImage captchaText={captcha.text} />
                    <button 
                      type="button" 
                      className={styles.refreshCaptchaButton}
                      onClick={refreshCaptcha}
                    >
                      <RefreshCw size={16} />
                      New Code
                    </button>
                  </div>
                  <input
                    type="text"
                    id="captcha"
                    name="captcha"
                    value={formData.captcha}
                    onChange={handleChange}
                    placeholder="Enter the code shown above"
                    className={`${styles.formInput} ${styles.captchaInput} ${errors.captcha ? styles.inputError : ''}`}
                    autoComplete="off"
                  />
                </div>
                {errors.captcha && <div className={styles.errorText}>{errors.captcha}</div>}
              </div>
            ) : (
              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.formLabel}>
                  <Lock size={18} />
                  Your Password
                </label>
                <div className={styles.passwordDescription}>
                  For security, please enter your password to verify your identity.
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className={`${styles.formInput} ${errors.password ? styles.inputError : ''}`}
                />
                {errors.password && <div className={styles.errorText}>{errors.password}</div>}
              </div>
            )}
            
            {submitError && (
              <div className={styles.submitErrorContainer}>
                <AlertTriangle size={18} />
                <div className={styles.submitErrorText}>{submitError}</div>
              </div>
            )}
            
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className={styles.loadingIcon} /> 
                  Sending Report...
                </>
              ) : (
                <>
                  <Send size={16} /> 
                  Submit Report
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportProblem;